/**
 * @fileoverview Manual KYC & AML check form — AML organisation/individual
 * screening (UBO + PEPs) and InfoTrackID remote identity verification.
 *
 * Subjects are prefilled from the job (company + identified officers) with a
 * manual-entry fallback. Orders submit through the same
 * `submitManualInfotrackSearch` mutation as registry searches.
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { toast } from "sonner";

type CheckProductKey =
  "aml_organisation_check" | "aml_individual_check" | "infotrackid_voi";

const CHECK_OPTIONS: ReadonlyArray<{ key: CheckProductKey; label: string }> = [
  {
    key: "aml_organisation_check",
    label: "AML check — organisation (UBO / PEPs)",
  },
  {
    key: "aml_individual_check",
    label: "AML check — individual (UBO / PEPs)",
  },
  { key: "infotrackid_voi", label: "Identity verification (remote VOI)" },
];

const MANUAL_SUBJECT = "__manual__";

/** Splits "Jane Q Doe" into first / middle / last for prefill. */
function splitName(full: string): {
  firstName: string;
  middleName: string;
  lastName: string;
} {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], middleName: "", lastName: "" };
  }
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
}

export function SearchesKycTrigger({ jobId }: { jobId: Id<"jobs"> }) {
  const { data: subjects } = useQuery({
    ...convexQuery(api.jobs.searches.queries.listKycSubjectsForJob, { jobId }),
  });

  const submitManual = useConvexMutation(
    api.jobs.searches.mutations.submitManualInfotrackSearch,
  );

  const [productKey, setProductKey] = useState<"" | CheckProductKey>("");
  const [subjectPick, setSubjectPick] = useState<string>(MANUAL_SUBJECT);

  // AML organisation
  const [orgNumber, setOrgNumber] = useState("");

  // Individual fields (shared by AML individual + VOI)
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");

  // AML search-type toggles
  const [includeUbo, setIncludeUbo] = useState(true);
  const [includePeps, setIncludePeps] = useState(true);

  // VOI-only contact details — typed each time (not stored on entities)
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");

  const resetSubjectFields = () => {
    setOrgNumber("");
    setFirstName("");
    setMiddleName("");
    setLastName("");
    setDob("");
    setEmail("");
    setMobile("");
    setSubjectPick(MANUAL_SUBJECT);
  };

  const applySubjectPick = (pick: string) => {
    setSubjectPick(pick);
    if (pick === MANUAL_SUBJECT) return;
    if (productKey === "aml_organisation_check") {
      setOrgNumber(subjects?.company?.acn ?? "");
      return;
    }
    const split = splitName(pick);
    setFirstName(split.firstName);
    setMiddleName(split.middleName);
    setLastName(split.lastName);
  };

  const searchTypes = [
    ...(includeUbo ? ["ubo"] : []),
    ...(includePeps ? ["peps"] : []),
  ];

  const isReady = (): boolean => {
    if (!productKey || commitMutation.isPending) return false;
    switch (productKey) {
      case "aml_organisation_check":
        return !!orgNumber.trim() && searchTypes.length > 0;
      case "aml_individual_check":
        return (
          !!firstName.trim() && !!lastName.trim() && searchTypes.length > 0
        );
      case "infotrackid_voi":
        return (
          !!firstName.trim() &&
          !!lastName.trim() &&
          !!email.trim() &&
          !!mobile.trim()
        );
    }
  };

  const buildPayload = (): Record<string, unknown> => {
    switch (productKey) {
      case "aml_organisation_check":
        return { organisationNumber: orgNumber.trim(), searchTypes };
      case "aml_individual_check":
        return {
          firstName: firstName.trim(),
          middleName: middleName.trim() || undefined,
          surname: lastName.trim(),
          dateOfBirth: dob || undefined,
          searchTypes,
        };
      case "infotrackid_voi":
        return {
          firstName: firstName.trim(),
          middleName: middleName.trim() || undefined,
          lastName: lastName.trim(),
          email: email.trim(),
          mobileNumber: mobile.trim(),
        };
      default:
        return {};
    }
  };

  const commitMutation = useMutation({
    mutationFn: async () =>
      submitManual({ jobId, productKey, inputPayload: buildPayload() }),
    onSuccess: () => {
      toast.success(
        productKey === "infotrackid_voi"
          ? "Verification link sent — the subject completes the check on their device."
          : "AML check submitted",
      );
      resetSubjectFields();
      setProductKey("");
    },
    onError: (e: Error) => toast.error(e.message || "Could not submit check."),
  });

  const isIndividual =
    productKey === "aml_individual_check" || productKey === "infotrackid_voi";
  const isAml =
    productKey === "aml_organisation_check" ||
    productKey === "aml_individual_check";

  return (
    <div className="space-y-4 rounded-lg border bg-background p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="kyc-check-type">Check type</Label>
          <Select
            value={productKey}
            onValueChange={(v) => {
              setProductKey(v as CheckProductKey);
              resetSubjectFields();
            }}
          >
            <SelectTrigger id="kyc-check-type">
              <SelectValue placeholder="Choose a check" />
            </SelectTrigger>
            <SelectContent>
              {CHECK_OPTIONS.map((o) => (
                <SelectItem key={o.key} value={o.key}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {productKey ? (
          <div className="space-y-2">
            <Label htmlFor="kyc-subject">Subject</Label>
            <Select value={subjectPick} onValueChange={applySubjectPick}>
              <SelectTrigger id="kyc-subject">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {productKey === "aml_organisation_check" ? (
                  subjects?.company ? (
                    <SelectItem value={subjects.company.name}>
                      {subjects.company.name}
                    </SelectItem>
                  ) : null
                ) : (
                  (subjects?.people ?? []).map((p) => (
                    <SelectItem key={p.name} value={p.name}>
                      {p.name}
                    </SelectItem>
                  ))
                )}
                <SelectItem value={MANUAL_SUBJECT}>Someone else…</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      {productKey === "aml_organisation_check" ? (
        <div className="space-y-2">
          <Label htmlFor="kyc-org-number">ACN / ABN</Label>
          <Input
            id="kyc-org-number"
            value={orgNumber}
            onChange={(e) => setOrgNumber(e.target.value)}
            placeholder={
              subjects?.company?.acn ?? "9-digit ACN or 11-digit ABN"
            }
          />
        </div>
      ) : null}

      {isIndividual ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="kyc-first">First name</Label>
            <Input
              id="kyc-first"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="kyc-middle">Middle name</Label>
            <Input
              id="kyc-middle"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="kyc-last">Last name</Label>
            <Input
              id="kyc-last"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
      ) : null}

      {productKey === "aml_individual_check" ? (
        <div className="space-y-2 sm:max-w-xs">
          <Label htmlFor="kyc-dob">Date of birth (optional)</Label>
          <DatePicker
            value={dob}
            onChange={(v) => setDob(v)}
            placeholder="Narrows matches"
          />
        </div>
      ) : null}

      {isAml ? (
        <div className="space-y-2">
          <Label>Search types</Label>
          <div className="flex items-center gap-6">
            <label
              htmlFor="kyc-ubo"
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <Checkbox
                id="kyc-ubo"
                checked={includeUbo}
                onCheckedChange={(v) => setIncludeUbo(v === true)}
              />
              Ultimate beneficial owners (UBO)
            </label>
            <label
              htmlFor="kyc-peps"
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <Checkbox
                id="kyc-peps"
                checked={includePeps}
                onCheckedChange={(v) => setIncludePeps(v === true)}
              />
              PEPs &amp; sanctions
            </label>
          </div>
        </div>
      ) : null}

      {productKey === "infotrackid_voi" ? (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            The subject receives an email link and completes identity
            verification on their own device (photo ID + 2FA). The report is
            imported automatically once verification finishes — this can take
            hours or days.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="kyc-email">Subject email</Label>
              <Input
                id="kyc-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="kyc-mobile">Subject mobile</Label>
              <Input
                id="kyc-mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="04xx xxx xxx"
              />
            </div>
          </div>
        </div>
      ) : null}

      <Button
        type="button"
        disabled={!isReady()}
        onClick={() => commitMutation.mutate()}
      >
        {productKey === "infotrackid_voi"
          ? "Send verification request"
          : "Run check"}
      </Button>
    </div>
  );
}
